'use client';

import { cn } from '@reelplicate/ui/lib/utils';
import { OTPInput, OTPInputContext } from 'input-otp';
import * as React from 'react';

import { tv } from '../../lib/tv';

/**
 * The Figma OTP Cell (OTP ↳ OTP Cell, 95:14), keyed on data attributes so
 * the same classes style a live slot and a static cell.
 *
 * 44×52, radius/md, Heading/H3 digit. Empty: 1px border/strong on bg/surface.
 * Filled: border/strong-hover. Focus (data-active): 1.5px border/focus +
 * Focus/Input halo + a 1.5×24 border/focus caret. Error: 1.5px
 * status/danger/fg stroke and digit. Disabled: bg/disabled, border/default.
 * 1.5px = 1px border + 0.5px inset ring, so nothing shifts.
 */
export const otpCellStyles = tv({
  slots: {
    cell: [
      'bg-bg-surface text-heading-h3 relative flex h-13 w-11 shrink-0 items-center justify-center rounded-md border',
      'transition-[border-color,box-shadow] duration-[120ms] ease-out',
    ],
    caret:
      'bg-border-focus animate-caret-blink pointer-events-none absolute h-6 w-[1.5px] motion-reduce:animate-none',
    separator: 'bg-border-strong h-0.5 w-2.5 shrink-0 rounded-full',
  },
  variants: {
    state: {
      Empty: { cell: 'border-border-strong text-text-primary' },
      Filled: { cell: 'border-border-strong-hover text-text-primary' },
      Focus: {
        cell: 'border-border-focus inset-ring-border-focus text-text-primary inset-ring-[0.5px]',
      },
      Error: {
        cell: 'border-status-danger-fg inset-ring-status-danger-fg text-status-danger-fg inset-ring-[0.5px]',
      },
      Disabled: { cell: 'border-border-default bg-bg-disabled text-text-disabled' },
    },
    /** The focused cell keeps its Focus/Input halo even when the code is wrong. */
    active: { true: { cell: 'shadow-focus-input' } },
  },
});

export type OTPCellState = 'Empty' | 'Focus' | 'Filled' | 'Error' | 'Disabled';

export type OTPCellPrimitiveProps = React.ComponentProps<'div'> & {
  char?: string | null;
  active?: boolean;
  caret?: boolean;
  invalid?: boolean;
  disabled?: boolean;
};

/** One cell, drawn from explicit state (shared by the live slot and static renders). */
function OTPCellPrimitive({
  char,
  active = false,
  caret,
  invalid,
  disabled,
  className,
  ...props
}: OTPCellPrimitiveProps) {
  const state: OTPCellState = disabled
    ? 'Disabled'
    : invalid
      ? 'Error'
      : active
        ? 'Focus'
        : char
          ? 'Filled'
          : 'Empty';
  const styles = otpCellStyles({ state, active: active && !disabled });
  return (
    <div
      data-slot="input-otp-slot"
      data-cell-state={state}
      data-active={active || undefined}
      data-disabled={disabled || undefined}
      className={styles.cell({ className })}
      {...props}
    >
      {char}
      {caret && <div data-slot="input-otp-caret" className={styles.caret()} />}
    </div>
  );
}

/** shadcn InputOTP (input-otp): one real input under a row of drawn cells. */
function InputOTP({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
}) {
  return (
    <OTPInput
      data-slot="input-otp"
      containerClassName={cn(
        'flex items-center gap-2 has-disabled:cursor-not-allowed',
        containerClassName,
      )}
      className={cn('disabled:cursor-not-allowed', className)}
      {...props}
    />
  );
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="input-otp-group"
      className={cn('flex items-center gap-2', className)}
      {...props}
    />
  );
}

/** A live cell: reads its character, focus and caret from the input. */
function InputOTPSlot({
  index,
  invalid,
  disabled,
  ...props
}: React.ComponentProps<'div'> & {
  index: number;
  invalid?: boolean;
  disabled?: boolean;
}) {
  const context = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = context?.slots[index] ?? {};
  return (
    <OTPCellPrimitive
      char={char}
      active={isActive}
      caret={hasFakeCaret}
      invalid={invalid}
      disabled={disabled}
      {...props}
    />
  );
}

/** Figma "Separator": a 10×2 border/strong bar between the 3 + 3 groups. */
function InputOTPSeparator({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="input-otp-separator"
      role="separator"
      className={otpCellStyles().separator({ className })}
      {...props}
    />
  );
}

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, OTPCellPrimitive };
